/**
 * Mascote miD — "o i".
 *
 * A mascote não é uma personagem nova: é o «i» da assinatura miD a ganhar
 * uma lente. O corpo nunca muda de forma nem se mexe — a expressão vive
 * apenas em duas peças, o pingo e a lente.
 *
 * Este componente segue o manual em `mid-mascote-ficha.html` (Ficha
 * partilhada pelo utilizador em Fev 2026):
 *
 *   Cores:
 *     • #3CC8B5  corpo + pingo (teal da marca)
 *     • #0E2C2A  interior da lente
 *     • #A78BD9  íris (roxo = a pessoa)
 *     • #1A1B2E  pupila
 *     • #E8A23C  âmbar (só no estado "encontrou")
 *     • #2E6660  versão inactiva / sem dados
 *
 *   Movimento (todas ease-in-out, excepto halo em ease-out):
 *     • Flutuação do pingo — sobe/desce ~4u, 3s, sempre activa
 *     • Piscar — pálpebra desce a cada 5,5s, 0,18s de duração (apenas
 *       nos estados "idle" e "reading" quando é natural)
 *     • Varrimento de leitura — linha roxa desce/sobe 1,9s, só no estado
 *       "reading"
 *     • Halo de validação — círculo teal cresce e desvanece 2,4s, só no
 *       estado "midStatus" (tem miD)
 *
 *   LOD por tamanho:
 *     ≥ 96 px  → completa
 *     48–95 px → sem brilho
 *     32–47 px → sem pupila
 *     ≤ 31 px  → só a lente
 *
 *   Regra de ouro: a mascote nunca tem boca. Se estiver a sorrir, está
 *   errada.
 *
 * Props:
 *   - state: 'idle' | 'reading' | 'thinking' | 'found' | 'midStatus' | 'empty'
 *   - size:  largura em pixels (aspect ratio 150:190 preservada)
 *   - className, ariaLabel
 */
import React from 'react';
import { motion } from 'framer-motion';
import { NEW_BRAND } from '@/lib/brand';
import { MarcoSvg } from './brand/marcoArt';

const TEAL       = '#3CC8B5';
const TEAL_TINT  = '#7FE3D3';
const TEAL_DEEP  = '#2AA492';
const LENS       = '#0E2C2A';
const IRIS       = '#A78BD9';
const PUPIL      = '#1A1B2E';
const AMBER      = '#E8A23C';
const INACTIVE   = '#2E6660';
const LID_INACT  = '#4E8C85';

const EASE = [0.42, 0, 0.58, 1]; // ease-in-out

function MidMascot({
  state = 'idle',
  size = 140,
  className = '',
  ariaLabel = 'Mascote miD',
  // Retrocompatibilidade: aceita props antigas do MidMascot roxo.
  // "variant"/"expression": 'happy' | 'waving' | 'wink' → mapeamos para
  // os estados oficiais da mascote miD.
  variant,
  expression,
  showSparkle: _showSparkle,
}) {
  // Se veio uma prop legacy, deriva o estado oficial (idempotente).
  if (!state || state === 'idle') {
    const legacy = variant || expression;
    if (legacy === 'waving') state = 'midStatus';
    else if (legacy === 'wink' || legacy === 'happy') state = 'idle';
  }

  // Zonamento: Marco. The six states are six of Marco's seven; anything else is idle.
  if (NEW_BRAND) {
    return <MarcoSvg state={state} size={size} className={className} aria-label={ariaLabel} data-testid="mid-mascot" data-state={state} />;
  }

  const height = Math.round((size * 190) / 150);

  // LOD (Level of detail) — segue o manual.
  const showBrilho  = size >= 96;
  const showPupila  = size >= 32;
  const showLenteOnly = size < 32;

  const isEmpty = state === 'empty';
  const isMidStatus = state === 'midStatus';
  const isFound = state === 'found';
  const isThinking = state === 'thinking';
  const isReading = state === 'reading';

  const bodyFill = isEmpty ? INACTIVE : TEAL;

  // "Só a lente" — versão minimalista para ≤ 31 px.
  if (showLenteOnly) {
    return (
      <svg
        role="img"
        aria-label={ariaLabel}
        width={size}
        height={size}
        viewBox="0 0 60 60"
        className={className}
        data-testid="mid-mascot"
        data-state={state}
      >
        <circle cx="30" cy="30" r="28" fill={bodyFill} />
        <circle cx="30" cy="30" r="15" fill={LENS} />
        {showPupila && <circle cx="30" cy="30" r="7" fill={IRIS} />}
      </svg>
    );
  }

  return (
    <svg
      role="img"
      aria-label={ariaLabel}
      width={size}
      height={height}
      viewBox="0 0 150 190"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      data-testid="mid-mascot"
      data-state={state}
    >
      <defs>
        <linearGradient id="mid-mascot-shine" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fff" stopOpacity="0.16" />
          <stop offset="60%" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
        <clipPath id="mid-mascot-lens-clip">
          <circle cx="75" cy="104" r="30" />
        </clipPath>
      </defs>

      {/* ── Sombra (imóvel — corpo nunca se mexe) ─────────────── */}
      <ellipse cx="75" cy="178" rx="42" ry="7" fill="#000" opacity={isEmpty ? 0.2 : 0.28} />

      {/* ── Halo de validação (só em "tem miD"). Cresce e desvanece. ── */}
      {isMidStatus && (
        <motion.circle
          cx="75"
          cy="104"
          r="30"
          fill={TEAL}
          initial={{ scale: 1, opacity: 0.35 }}
          animate={{ scale: [1, 1.6, 2.1], opacity: [0.35, 0.18, 0] }}
          transition={{ duration: 2.4, ease: 'easeOut', repeat: Infinity }}
          style={{ transformOrigin: '75px 104px' }}
        />
      )}

      {/* ── PINGO / "cabeça" — flutua 4u (~4.75px) em 3s ─────── */}
      {/* Estado por defeito: círculo cheio.
          Encontrou: ponto de exclamação âmbar.
          Tem miD: visto verde.
          A pensar: 3 reticências que pulsam.
          Sem dados: desaturado. */}
      <PingoLayer state={state} isEmpty={isEmpty} isFound={isFound}
        isMidStatus={isMidStatus} isThinking={isThinking} />

      {/* ── CORPO (haste do i) — nunca muda ──────────────────── */}
      <rect x="35" y="54" width="80" height="118" rx="34" fill={bodyFill} />
      {!isEmpty && (
        <rect x="35" y="54" width="80" height="118" rx="34" fill="url(#mid-mascot-shine)" />
      )}

      {/* ── LENTE ────────────────────────────────────────────── */}
      <circle cx="75" cy="104" r="30" fill={LENS} />
      {!isEmpty && (
        <circle cx="75" cy="104" r="30" fill="none" stroke={TEAL_DEEP} strokeWidth="3" />
      )}

      {/* Sem dados: olho fechado, traço horizontal simples. */}
      {isEmpty && (
        <path d="M60,104 h30" stroke={LID_INACT} strokeWidth="7" strokeLinecap="round" />
      )}

      {/* Tem miD: olho fecha em arco (sorriso do olho, não boca). */}
      {isMidStatus && (
        <path
          d="M60,110 q15,-18 30,0"
          stroke={IRIS}
          strokeWidth="7"
          fill="none"
          strokeLinecap="round"
        />
      )}

      {/* Leitura: interior vira documento com linha roxa a varrer. */}
      {isReading && (
        <g clipPath="url(#mid-mascot-lens-clip)">
          <rect x="45" y="88" width="60" height="32" rx="4" fill="#1A3B38" />
          <path d="M54,96 h24 M54,112 h34" stroke={TEAL_DEEP} strokeWidth="3" strokeLinecap="round" />
          {/* Scanline roxa: sobe/desce dentro da lente, 1,9s */}
          <motion.rect
            x="45"
            width="60"
            height="3.5"
            rx="2"
            fill={IRIS}
            animate={{ y: [86, 118, 86] }}
            transition={{ duration: 1.9, ease: 'easeInOut', repeat: Infinity }}
          />
        </g>
      )}

      {/* Íris + pupila + brilho. Só quando faz sentido (não em empty/mid/reading). */}
      {!isEmpty && !isMidStatus && !isReading && (
        <IrisLayer state={state} showBrilho={showBrilho} showPupila={showPupila}
          isFound={isFound} isThinking={isThinking} />
      )}

      {/* ── Pálpebra: piscar 0,18s a cada 5,5s. Apenas em idle. ─ */}
      {state === 'idle' && (
        <motion.rect
          x="45"
          width="60"
          rx="8"
          fill={TEAL}
          initial={{ y: 74, height: 0 }}
          animate={{
            y:      [74, 74, 74, 74],
            height: [0, 0, 60, 0],
          }}
          transition={{
            duration: 5.5,
            times:    [0, 0.955, 0.972, 1],
            ease:     EASE,
            repeat:   Infinity,
          }}
        />
      )}
    </svg>
  );
}

/* ─── Pingo por estado ─────────────────────────────────────── */
function PingoLayer({ state, isEmpty, isFound, isMidStatus, isThinking }) {
  if (isEmpty) {
    // Só um círculo dessaturado, sem tint nem movimento.
    return <circle cx="75" cy="30" r="13" fill={INACTIVE} />;
  }

  // Movimento comum: pingo flutua ~4u em 3s.
  const float = {
    animate: { y: [0, -4.75, 0] },
    transition: { duration: 3, ease: 'easeInOut', repeat: Infinity },
  };

  if (isFound) {
    // Ponto de exclamação âmbar.
    return (
      <motion.g {...float}>
        <rect x="69" y="6"  width="12" height="22" rx="6" fill={AMBER} />
        <circle cx="75" cy="37" r="6.5" fill={AMBER} />
      </motion.g>
    );
  }

  if (isMidStatus) {
    // Visto verde.
    return (
      <motion.g {...float}>
        <path
          d="M66,26 l6,7 l13,-15"
          stroke={TEAL}
          strokeWidth="8"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </motion.g>
    );
  }

  if (isThinking) {
    // Três reticências pulsantes (o "pingo" divide-se).
    return (
      <g transform="translate(0,-2)">
        <motion.circle cx="55" cy="26" r="7" fill={TEAL}
          animate={{ opacity: [0.35, 1, 0.35] }}
          transition={{ duration: 1.5, ease: 'easeInOut', repeat: Infinity, delay: 0 }} />
        <motion.circle cx="75" cy="26" r="7" fill={TEAL}
          animate={{ opacity: [0.35, 1, 0.35] }}
          transition={{ duration: 1.5, ease: 'easeInOut', repeat: Infinity, delay: 0.25 }} />
        <motion.circle cx="95" cy="26" r="7" fill={TEAL}
          animate={{ opacity: [0.35, 1, 0.35] }}
          transition={{ duration: 1.5, ease: 'easeInOut', repeat: Infinity, delay: 0.5 }} />
      </g>
    );
  }

  // Estado por defeito ("em repouso" / "idle" / "reading"):
  // pingo + tint superior a flutuar.
  return (
    <motion.g {...float}>
      <circle cx="75" cy="26" r="16" fill={TEAL} />
      <circle cx="70" cy="21" r="5"  fill={TEAL_TINT} opacity="0.85" />
    </motion.g>
  );
}

/* ─── Íris + pupila + brilhos ──────────────────────────────── */
function IrisLayer({ state, showBrilho, showPupila, isFound, isThinking }) {
  // Encontrou: pupila dilata, íris âmbar.
  if (isFound) {
    return (
      <>
        <circle cx="75" cy="104" r="20" fill={AMBER} />
        {showPupila && <circle cx="75" cy="104" r="9" fill={PUPIL} />}
        {showBrilho && <circle cx="67" cy="96" r="5.5" fill="#fff" opacity="0.9" />}
      </>
    );
  }

  // A pensar: olhar desviado para cima e direita.
  if (isThinking) {
    return (
      <>
        <circle cx="82" cy="99" r="16" fill={IRIS} />
        {showPupila && <circle cx="82" cy="99" r="7" fill={PUPIL} />}
        {showBrilho && <circle cx="76" cy="93" r="4.5" fill="#fff" opacity="0.9" />}
      </>
    );
  }

  // Em repouso: íris centrada. Nada se mexe além do pisco.
  return (
    <>
      <circle cx="75" cy="104" r="16" fill={IRIS} />
      {showPupila && <circle cx="75" cy="104" r="7" fill={PUPIL} />}
      {showBrilho && (
        <>
          <circle cx="68" cy="97"  r="5"   fill="#fff" opacity="0.9" />
          <circle cx="83" cy="112" r="2.6" fill="#fff" opacity="0.5" />
        </>
      )}
    </>
  );
}


/* ─── Exports ────────────────────────────────────────────────
   Alguns componentes legacy (MatchmakerHome, etc.) importam por nome. */
export default MidMascot;
export { MidMascot };

/**
 * MidGift — ilustração de presente teal usada no Bónus Diário do
 * cockpit (`MatchmakerHome`). Não faz parte do sistema formal da mascote,
 * é uma micro-ilustração de suporte.
 */
export function MidGift({ size = 64, className = '' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" className={className} aria-hidden="true">
      <defs>
        <linearGradient id="mid-gift-shadow" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fff" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#000" stopOpacity="0.2" />
        </linearGradient>
      </defs>
      {/* Caixa */}
      <rect x="8" y="28" width="48" height="30" rx="3" fill="#38B2AC" />
      <rect x="8" y="28" width="48" height="30" rx="3" fill="url(#mid-gift-shadow)" opacity="0.2" />
      {/* Tampa */}
      <rect x="6" y="24" width="52" height="10" rx="2" fill="#2C8F8A" />
      {/* Fita vertical */}
      <rect x="28" y="24" width="8" height="34" fill="#F3F1FB" />
      {/* Fita horizontal */}
      <rect x="6" y="28" width="52" height="5" fill="#F3F1FB" />
      {/* Laço */}
      <circle cx="26" cy="22" r="6" fill="#8B80D8" />
      <circle cx="38" cy="22" r="6" fill="#8B80D8" />
      <circle cx="32" cy="22" r="3" fill="#2C8F8A" />
    </svg>
  );
}
